<!-- DarkModeToggle.vue (template unchanged) -->
<template>
  <v-switch
    v-model="isDark"
    inset
    hide-details
    density="comfortable"
    class="theme-toggle"
    :true-icon="'mdi-moon-waxing-crescent'"
    :false-icon="'mdi-white-balance-sunny'"
    @update:model-value="onToggle"
    :aria-label="isDark ? 'Dark mode' : 'Light mode'"
  />
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useTheme } from 'vuetify'
import { useApplyDarkMode } from '@/utils/theme'

const applyDarkMode = useApplyDarkMode()
const vuetifyTheme = useTheme()

const isDark = ref(false)
// accept boolean | null and coerce null to a boolean
function onToggle(value: boolean | null) { applyDarkMode(!!value) }

onMounted(() => {
  isDark.value = !!vuetifyTheme.global.current.value.dark
  applyDarkMode(isDark.value)
})
</script>

<style scoped lang="scss">
.theme-toggle {
  --toggle-height: 32px;
  --toggle-width: 64px;
  --thumb-size: 26px;

  flex: 0 0 auto;
  :deep(.v-input__control) { width: auto; }
  :deep(.v-selection-control) { margin: 0; }

  /* Track */
  :deep(.v-switch__track) {
    width: var(--toggle-width);
    height: var(--toggle-height);
    border-radius: calc(var(--toggle-height) / 2);
    border: 1px solid rgba(0,0,0,.12);
    background: #fff; /* LIGHT track */
    transition: background-color .2s ease, border-color .2s ease;
  }
  /* DARK (checked) track */
  :deep(.v-selection-control--dirty .v-switch__track) {
    background: #242C2E;;
    border-color: rgba(255,255,255,.14);
  }

  /* Thumb default = LIGHT state */
  :deep(.v-switch__thumb) {
    width: var(--thumb-size);
    height: var(--thumb-size);
    border-radius: 50%;
    background: #fff; /* white thumb in LIGHT */
    box-shadow: 0 2px 6px rgba(0,0,0,.25);
    display: grid; place-items: center;
    transition: transform .2s ease, background-color .2s ease;

    .v-icon { color: var(--v-theme-on-surface) !important; font-size: 18px; }
  }
  /* DARK (checked) thumb */
  :deep(.v-selection-control--dirty .v-switch__thumb) {
    background: var(--v-theme-primary);             /* colored thumb */
  }
  :deep(.v-selection-control--dirty .v-switch__thumb .v-icon) {
    color: #fff !important;                         /* moon icon white */
  }
}
</style>
